import { useEffect, useLayoutEffect, useState } from 'react'
import {
  TWidgetPosition,
  getLogoPositions,
  useLogoPosition,
} from '../Controls/useLogoPosition.hook'
import { ArtworkWidget } from './ArtworkWidget'
import { colorInnerSvgPaths } from './Widgets.utils'
import { useCurrentCanvasSide, useWidget } from '../EditorDataProvider'
import { calculateAssetSize } from '../editor.config'
import { loadImage } from '../editor.utils'
import { useSelectedFile } from '@pageFeatures/design/designUpload/components/UploadDropzone/UploadDropzone.provider'

export interface ILogoWidget {
  assetSvgData: string
  assetUrl?: string
  className?: string
  color: string
  positionIsConfigurable?: boolean
  position: {
    x: number
    y: number
  }
  positionKey?: TWidgetPosition
  size: {
    w: number
    h: number
  }
  rotation: number
  scaleStepCount: number
  multiColorMode?: boolean
}

export function LogoWidget({
  assetSvgData,
  position,
  color,
  className = '',
  positionKey = 'center',
  rotation,
  size,
  scaleStepCount = 1,
  multiColorMode,
  assetUrl,
}: ILogoWidget) {
  const [coloredSvgData, setColoredSvgData] = useState<string>(assetSvgData)
  const { canvasConfig } = useCurrentCanvasSide()
  const { updateWidget } = useWidget('logo')
  const [assetSize, setAssetSize] = useState<{ w: number; h: number }>(size)
  const { selectedFile } = useSelectedFile()
  const [assetRatioFactor, setAssetRatioFactor] = useState(1)

  const logoPositions = useLogoPosition({
    canvasConfig,
    widgetSize: assetSize,
  })

  useLayoutEffect(() => {
    const { assetWidth, assetHeight } = calculateAssetSize(
      canvasConfig,
      scaleStepCount,
      {
        w: Math.min(canvasConfig?.width, canvasConfig?.height) * 0.35,
        h:
          (Math.min(canvasConfig?.width, canvasConfig?.height) * 0.35) /
          (assetRatioFactor || 1),
      }
    )

    setAssetSize({
      w: assetWidth,
      h: assetHeight,
    })
  }, [
    canvasConfig?.width,
    canvasConfig?.height,
    scaleStepCount,
    assetRatioFactor,
    multiColorMode,
  ])

  useLayoutEffect(() => {
    const newPosition = getLogoPositions({
      canvasConfig,
      widgetSize: assetSize,
    })

    updateWidget({
      position: newPosition?.[positionKey],
      size: assetSize,
    })
  }, [assetSize])

  useLayoutEffect(() => {
    if (logoPositions?.[positionKey]) {
      updateWidget({ position: logoPositions?.[positionKey] })
    }
  }, [positionKey])

  useLayoutEffect(() => {
    const coloredSvgData = colorInnerSvgPaths(assetSvgData, color)
    setColoredSvgData(coloredSvgData)
  }, [assetSvgData, color])

  useEffect(() => {
    setAssetRatioFactor(
      selectedFile?.metaData?.width / selectedFile?.metaData?.height || 1
    )
  }, [selectedFile])

  return (
    /** Checking for position to be initialized to avoid logo
     * jump between initial position and the actual one,
     * calculated based on the size. */
    position?.x !== undefined &&
    position?.y !== undefined && (
      <ArtworkWidget
        assetSvgData={coloredSvgData}
        position={position}
        rotation={rotation}
        color={color}
        size={assetSize}
        className={className}
        assetUrl={assetUrl || selectedFile?.fileAsDataUrl}
        multiColorMode={multiColorMode}
      />
    )
  )
}
